import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CalendarClock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { compareWithLegacy, UNIT_LABELS } from '@/lib/deadlines'
import { formatDateBr } from '@/lib/publication-inbox'
import * as toast from '@/lib/toast'
import { DeadlineChangedError, deadlineService, type DeadlineRecord } from '@/services/deadline-service'
import { CalculationMemoryPopover } from './CalculationMemoryPopover'
import { ManualDeadlineForm } from './ManualDeadlineForm'

export interface DeadlinePublication {
  id: number
  siglaTribunal: string
  dataDisponibilizacao: string
  numeroProcesso: string
  nomeOrgao?: string | null
}

const errorMessage = (error: unknown) =>
  (error as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Não foi possível concluir'

/**
 * Prazo da publicação no painel lateral: o sugerido na importação (ou lançado
 * à mão), com a memória do cálculo e o botão "Lançar prazo" (confirmação
 * humana que cria a tarefa). O cálculo antigo do navegador aparece só se divergir.
 */
export function PublicationDeadlinePanel({ publication }: { publication: DeadlinePublication }) {
  const [record, setRecord] = useState<DeadlineRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const records = await deadlineService.listForPublication(publication.id)
      setRecord(records[0] ?? null)
    } catch {
      setRecord(null)
    } finally {
      setLoading(false)
    }
  }, [publication.id])

  useEffect(() => { void load() }, [load])

  const confirm = async () => {
    if (!record) return
    setBusy(true)
    try {
      setRecord(await deadlineService.confirm(record.id))
      toast.success('Prazo lançado', 'A tarefa de prazo processual foi criada para você.')
    } catch (error) {
      if (error instanceof DeadlineChangedError) {
        setRecord(error.record)
        toast.error('O cálculo mudou', 'O calendário foi alterado desde a sugestão. Confira o novo prazo e lance de novo.')
      } else {
        toast.error('Prazo não lançado', errorMessage(error))
      }
    } finally {
      setBusy(false)
    }
  }

  const discard = async () => {
    if (!record) return
    setBusy(true)
    try {
      await deadlineService.discard(record.id)
      setRecord(null)
    } catch (error) {
      toast.error('Não foi possível descartar', errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Carregando prazo…</p>

  if (!record) {
    return (
      <section aria-label="Prazo" className="rounded-lg border p-3">
        <p className="mb-2 text-sm text-muted-foreground">Nenhum prazo sugerido para esta publicação.</p>
        <ManualDeadlineForm publication={publication} onCreated={setRecord} />
      </section>
    )
  }

  const legacy = compareWithLegacy(record)
  const confirmed = record.status === 'CONFIRMADO'
  return (
    <section aria-label="Prazo" className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="size-4" aria-hidden />
        <span className="text-sm font-medium">{confirmed ? 'Prazo lançado' : 'Prazo sugerido'}</span>
        <Badge variant="outline">{record.dias} {UNIT_LABELS[record.unidade]}</Badge>
        <Badge variant="secondary">{record.tribunal}{record.uf ? `/${record.uf}` : ''}</Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Prazo fatal</dt>
        <dd className="font-semibold tabular-nums">{formatDateBr(record.prazoFatal)}</dd>
        <dt className="text-muted-foreground">Prazo interno</dt>
        <dd className="tabular-nums">{formatDateBr(record.prazoInterno)}</dd>
        <dt className="text-muted-foreground">Publicação / início</dt>
        <dd className="tabular-nums">{formatDateBr(record.dataPublicacao)} · {formatDateBr(record.dataInicio)}</dd>
      </dl>
      {record.prazoFatalLegal && (
        <p role="alert" className="flex gap-1 text-xs text-destructive">
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
          Há dia “a conferir” no calendário: o prazo fatal acima é a leitura mais segura. Contando esse dia, venceria em {formatDateBr(record.prazoFatalLegal)}.
        </p>
      )}
      {legacy?.differs && (
        <p role="note" className="flex gap-1 text-xs text-destructive">
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
          Cálculo antigo (sem feriados): {formatDateBr(legacy.legacy)} · cálculo do servidor: {formatDateBr(record.prazoFatal)}. Vale o do servidor.
        </p>
      )}
      {record.trechoDetectado && <p className="text-xs italic text-muted-foreground">“{record.trechoDetectado}”</p>}
      <div className="flex flex-wrap items-center gap-2">
        <CalculationMemoryPopover memoria={record.memoria} />
        {!confirmed && (
          <>
            <Button size="sm" onClick={confirm} loading={busy}>Lançar prazo</Button>
            <Button size="sm" variant="ghost" onClick={discard} disabled={busy}>Descartar</Button>
          </>
        )}
        {confirmed && record.taskId && <span className="text-xs text-muted-foreground">Tarefa #{record.taskId}</span>}
      </div>
    </section>
  )
}
